(() => {
  if (window.__p3dCategoryCarouselLoaded) return;
  window.__p3dCategoryCarouselLoaded = true;

  const SELECTOR = '[data-p3d-cc]';
  const instances = new Map();

  const horizontalMap = {
    left: { align: 'flex-start', text: 'left' },
    center: { align: 'center', text: 'center' },
    right: { align: 'flex-end', text: 'right' }
  };

  const verticalMap = {
    top: 'flex-start',
    middle: 'center',
    bottom: 'flex-end'
  };

  class P3DCategoryCarousel {
    constructor(section) {
      this.section = section;
      this.viewport = section.querySelector('[data-p3d-cc-viewport]');
      this.track = section.querySelector('[data-p3d-cc-track]');
      this.prevButton = section.querySelector('[data-p3d-cc-prev]');
      this.nextButton = section.querySelector('[data-p3d-cc-next]');
      this.originalSlides = Array.from(this.track.querySelectorAll('[data-p3d-cc-slide]'));
      this.originalCount = this.originalSlides.length;

      this.sectionObserver = null;
      this.slideObserver = null;
      this.attrObserver = null;
      this.resizeRaf = null;
      this.scrollEndTimer = null;
      this.isActivated = false;
      this.isJumping = false;

      this.onResize = this.onResize.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onPrev = this.onPrev.bind(this);
      this.onNext = this.onNext.bind(this);

      this.applySettings();
      this.setupLoop();
      this.bindEvents();
      this.observeSection();
      this.observeAttributeChanges();
    }

    applySettings() {
      const d = this.section.dataset;

      this.section.style.setProperty('--p3dcc-bg', d.bgColor || '#ffffff');
      this.section.style.setProperty('--p3dcc-title-color', d.titleColor || '#111111');
      this.section.style.setProperty('--p3dcc-padding-top-desktop', `${parseInt(d.paddingTopDesktop || '24', 10)}px`);
      this.section.style.setProperty('--p3dcc-padding-top-mobile', `${parseInt(d.paddingTopMobile || '20', 10)}px`);
      this.section.style.setProperty('--p3dcc-gap-title-carousel-desktop', `${parseInt(d.titleCarouselGapDesktop || '24', 10)}px`);
      this.section.style.setProperty('--p3dcc-gap-title-carousel-mobile', `${parseInt(d.titleCarouselGapMobile || '20', 10)}px`);
      this.section.style.setProperty('--p3dcc-padding-bottom-desktop', `${parseInt(d.paddingBottomDesktop || '24', 10)}px`);
      this.section.style.setProperty('--p3dcc-padding-bottom-mobile', `${parseInt(d.paddingBottomMobile || '20', 10)}px`);
      this.section.style.setProperty('--p3dcc-title-size-desktop', `${parseInt(d.titleSizeDesktop || '32', 10)}px`);
      this.section.style.setProperty('--p3dcc-title-size-mobile', `${parseInt(d.titleSizeMobile || '24', 10)}px`);
      this.section.style.setProperty('--p3dcc-card-height-desktop', `${parseInt(d.cardHeightDesktop || '520', 10)}px`);
      this.section.style.setProperty('--p3dcc-card-height-mobile', `${parseInt(d.cardHeightMobile || '420', 10)}px`);
      this.section.style.setProperty('--p3dcc-card-radius', `${parseInt(d.cardRadius || '18', 10)}px`);
      this.section.style.setProperty('--p3dcc-gap-desktop', `${parseInt(d.cardGapDesktop || '18', 10)}px`);
      this.section.style.setProperty('--p3dcc-gap-mobile', `${parseInt(d.cardGapMobile || '12', 10)}px`);
      this.section.style.setProperty('--p3dcc-card-title-size-desktop', `${parseInt(d.cardTitleSizeDesktop || d.cardTitleSize || '24', 10)}px`);
      this.section.style.setProperty('--p3dcc-card-title-size-mobile', `${parseInt(d.cardTitleSizeMobile || d.cardTitleSize || '24', 10)}px`);
      this.section.style.setProperty('--p3dcc-card-text-inset-desktop', `${parseInt(d.cardTextInsetDesktop || '22', 10)}px`);
      this.section.style.setProperty('--p3dcc-card-text-inset-mobile', `${parseInt(d.cardTextInsetMobile || '18', 10)}px`);
      this.section.style.setProperty('--p3dcc-overlay-base-opacity-desktop', `${(parseInt(d.overlayBaseOpacityDesktop || '0', 10) / 100).toFixed(2)}`);
      this.section.style.setProperty('--p3dcc-overlay-base-opacity-mobile', `${(parseInt(d.overlayBaseOpacityMobile || '0', 10) / 100).toFixed(2)}`);
      this.section.style.setProperty('--p3dcc-overlay-side-opacity-desktop', `${(parseInt(d.overlaySideOpacityDesktop || '70', 10) / 100).toFixed(2)}`);
      this.section.style.setProperty('--p3dcc-overlay-side-opacity-mobile', `${(parseInt(d.overlaySideOpacityMobile || '75', 10) / 100).toFixed(2)}`);
      this.section.style.setProperty('--p3dcc-arrow-color', d.arrowColor || '#111111');
      this.section.style.setProperty('--p3dcc-arrow-bg', d.arrowBgColor || '#f2f2f2');

      const hDesktop = horizontalMap[d.textHAlignDesktop || 'left'] || horizontalMap.left;
      const hMobile = horizontalMap[d.textHAlignMobile || 'left'] || horizontalMap.left;
      const vDesktop = verticalMap[d.textVAlignDesktop || 'bottom'] || verticalMap.bottom;
      const vMobile = verticalMap[d.textVAlignMobile || 'bottom'] || verticalMap.bottom;

      this.section.style.setProperty('--p3dcc-caption-align-desktop', hDesktop.align);
      this.section.style.setProperty('--p3dcc-caption-text-desktop', hDesktop.text);
      this.section.style.setProperty('--p3dcc-caption-justify-desktop', vDesktop);
      this.section.style.setProperty('--p3dcc-caption-align-mobile', hMobile.align);
      this.section.style.setProperty('--p3dcc-caption-text-mobile', hMobile.text);
      this.section.style.setProperty('--p3dcc-caption-justify-mobile', vMobile);
    }

    observeAttributeChanges() {
      this.attrObserver = new MutationObserver((mutations) => {
        const hasDataChange = mutations.some((m) => m.type === 'attributes' && m.attributeName && m.attributeName.startsWith('data-'));
        if (!hasDataChange) return;

        this.applySettings();

        if (this.isActivated) {
          this.loadVisibleSlides();
          this.updateArrows();
        }
      });

      this.attrObserver.observe(this.section, { attributes: true });
    }

    setupLoop() {
      if (this.originalCount <= 1) return;
      if (this.track.dataset.loopReady === 'true') return;

      const originals = Array.from(this.track.children);
      const prependFragment = document.createDocumentFragment();
      const appendFragment = document.createDocumentFragment();

      originals.forEach((slide) => {
        const appendClone = slide.cloneNode(true);
        appendClone.dataset.clone = 'true';
        appendFragment.appendChild(appendClone);
      });

      originals.slice().reverse().forEach((slide) => {
        const prependClone = slide.cloneNode(true);
        prependClone.dataset.clone = 'true';
        prependFragment.insertBefore(prependClone, prependFragment.firstChild);
      });

      this.track.insertBefore(prependFragment, this.track.firstChild);
      this.track.appendChild(appendFragment);
      this.track.dataset.loopReady = 'true';

      requestAnimationFrame(() => {
        this.jumpToOriginalStart();
      });
    }

    jumpToOriginalStart() {
      if (this.originalCount <= 1) return;
      const firstOriginal = this.track.children[this.originalCount];
      if (!firstOriginal) return;

      this.setScrollInstant(firstOriginal.offsetLeft);
    }

    setScrollInstant(value) {
      const previous = this.viewport.style.scrollBehavior;
      this.viewport.style.scrollBehavior = 'auto';
      this.viewport.scrollLeft = value;
      this.viewport.style.scrollBehavior = previous || '';
    }

    bindEvents() {
      if (this.prevButton) this.prevButton.addEventListener('click', this.onPrev);
      if (this.nextButton) this.nextButton.addEventListener('click', this.onNext);

      this.viewport.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);
    }

    observeSection() {
      this.sectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) this.activate();
          });
        },
        {
          root: null,
          rootMargin: '0px 0px 0px 0px',
          threshold: 0.08
        }
      );

      this.sectionObserver.observe(this.section);
    }

    activate() {
      if (this.isActivated) return;
      this.isActivated = true;
      this.section.classList.add('is-activated');

      requestAnimationFrame(() => {
        this.loadInitialSlides();
        this.observeSlides();
        this.updateArrows();
      });

      if (this.sectionObserver) this.sectionObserver.disconnect();
    }

    observeSlides() {
      this.slideObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) this.loadSlide(entry.target);
          });
        },
        {
          root: this.viewport,
          rootMargin: '0px 60% 0px 60%',
          threshold: 0.01
        }
      );

      this.getAllSlides().forEach((slide) => {
        this.slideObserver.observe(slide);
      });
    }

    getAllSlides() {
      return Array.from(this.track.querySelectorAll('[data-p3d-cc-slide]'));
    }

    getNearViewportSlides() {
      const viewportRect = this.viewport.getBoundingClientRect();

      return this.getAllSlides().filter((slide) => {
        const rect = slide.getBoundingClientRect();
        return (
          rect.right >= viewportRect.left - viewportRect.width * 0.5 &&
          rect.left <= viewportRect.right + viewportRect.width * 0.5
        );
      });
    }

    loadInitialSlides() {
      const slides = this.getNearViewportSlides();

      slides.forEach((slide, index) => {
        window.setTimeout(() => {
          this.loadSlide(slide);
        }, index * 90);
      });
    }

    loadVisibleSlides() {
      const viewportRect = this.viewport.getBoundingClientRect();

      this.getAllSlides().forEach((slide) => {
        const rect = slide.getBoundingClientRect();
        const isNear =
          rect.right >= viewportRect.left - viewportRect.width &&
          rect.left <= viewportRect.right + viewportRect.width;

        if (isNear) this.loadSlide(slide);
      });
    }

    loadSlide(slide) {
      const img = slide.querySelector('[data-p3d-cc-image]');
      if (!img || img.dataset.loaded === 'true') return;

      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      const src = isMobile ? img.dataset.mobileSrc : img.dataset.desktopSrc;
      const srcset = isMobile ? img.dataset.mobileSrcset : img.dataset.desktopSrcset;
      const sizes = img.dataset.sizes || '100vw';

      if (!src) return;

      img.sizes = sizes;
      if (srcset) img.srcset = srcset;
      img.src = src;

      img.addEventListener(
        'load',
        () => {
          slide.classList.add('is-media-loaded');
        },
        { once: true }
      );

      img.dataset.loaded = 'true';
    }

    getStepSize() {
      const firstReal = this.track.children[this.originalCount] || this.track.children[0];
      if (!firstReal) return 0;

      const styles = window.getComputedStyle(this.track);
      const gap = parseFloat(styles.columnGap || styles.gap || '0');
      return firstReal.getBoundingClientRect().width + gap;
    }

    onPrev() {
      this.scrollByStep(-1);
    }

    onNext() {
      this.scrollByStep(1);
    }

    scrollByStep(direction) {
      const step = this.getStepSize();
      if (!step) return;

      this.viewport.scrollBy({
        left: step * direction,
        behavior: 'smooth'
      });
    }

    onScroll() {
      if (!this.isActivated || this.originalCount <= 1) return;
      if (this.isJumping) return;

      this.handleInfiniteBounds();
      this.loadVisibleSlides();

      window.clearTimeout(this.scrollEndTimer);
      this.scrollEndTimer = window.setTimeout(() => {
        this.handleInfiniteBounds();
      }, 140);
    }

    handleInfiniteBounds() {
      const firstOriginal = this.track.children[this.originalCount];
      const firstAppendedSet = this.track.children[this.originalCount * 2];
      if (!firstOriginal || !firstAppendedSet) return;

      const originalsStart = firstOriginal.offsetLeft;
      const appendedStart = firstAppendedSet.offsetLeft;
      const originalsWidth = appendedStart - originalsStart;
      const threshold = this.getStepSize() * 0.5;
      const currentLeft = this.viewport.scrollLeft;

      if (currentLeft < originalsStart - threshold) {
        this.performLoopJump(currentLeft + originalsWidth);
      } else if (currentLeft >= appendedStart - threshold) {
        this.performLoopJump(currentLeft - originalsWidth);
      }
    }

    performLoopJump(target) {
      this.isJumping = true;
      const previous = this.viewport.style.scrollBehavior;
      this.viewport.style.scrollBehavior = 'auto';
      this.viewport.scrollLeft = target;

      requestAnimationFrame(() => {
        this.viewport.style.scrollBehavior = previous || '';
        this.isJumping = false;
      });
    }

    updateArrows() {
      if (!this.prevButton || !this.nextButton) return;
      const disabled = this.originalCount < 2;
      this.prevButton.disabled = disabled;
      this.nextButton.disabled = disabled;
    }

    onResize() {
      if (this.resizeRaf) cancelAnimationFrame(this.resizeRaf);

      this.resizeRaf = requestAnimationFrame(() => {
        this.applySettings();

        if (this.isActivated) {
          this.loadVisibleSlides();
          if (this.originalCount > 1) this.handleInfiniteBounds();
        }
      });
    }

    scrollToBlock(blockId) {
      if (!this.isActivated) this.activate();

      const target = Array.from(this.track.children).find(
        (slide) => slide.dataset.blockId === blockId && slide.dataset.clone !== 'true'
      );

      if (!target) return;

      this.viewport.scrollTo({
        left: target.offsetLeft,
        behavior: 'smooth'
      });

      this.loadSlide(target);
    }

    destroy() {
      if (this.prevButton) this.prevButton.removeEventListener('click', this.onPrev);
      if (this.nextButton) this.nextButton.removeEventListener('click', this.onNext);

      this.viewport.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);

      if (this.sectionObserver) this.sectionObserver.disconnect();
      if (this.slideObserver) this.slideObserver.disconnect();
      if (this.attrObserver) this.attrObserver.disconnect();

      window.clearTimeout(this.scrollEndTimer);
      if (this.resizeRaf) cancelAnimationFrame(this.resizeRaf);
    }
  }

  function init(scope = document) {
    const sections = scope.matches?.(SELECTOR)
      ? [scope]
      : Array.from(scope.querySelectorAll?.(SELECTOR) || []);

    sections.forEach((section) => {
      if (instances.has(section)) return;
      instances.set(section, new P3DCategoryCarousel(section));
    });
  }

  function destroy(scope = document) {
    const sections = scope.matches?.(SELECTOR)
      ? [scope]
      : Array.from(scope.querySelectorAll?.(SELECTOR) || []);

    sections.forEach((section) => {
      const instance = instances.get(section);
      if (!instance) return;
      instance.destroy();
      instances.delete(section);
    });
  }

  function ready(callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  }

  ready(() => init(document));

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    destroy(event.target);
  });

  document.addEventListener('shopify:block:select', (event) => {
    const { sectionId, blockId } = event.detail || {};
    if (!sectionId || !blockId) return;

    instances.forEach((instance) => {
      if (instance.section.dataset.sectionId === sectionId) {
        instance.scrollToBlock(blockId);
      }
    });
  });
})();
